/**
 * embedMode — utilities for detecting whether V2 is running inside the V3
 * shell as an iframe, and for querying the state of the V3 session bridge.
 *
 * When VITE_ENABLE_LEGACY_IFRAME=true on the V3 side, V3 mounts V2 at a
 * different origin in an <iframe>.  In that mode V3 is the sole auth owner:
 *  - V3 handles Keycloak login, SSO OTP, and Terms acceptance.
 *  - V3 pushes credentials to V2 via postMessage (v3SessionBridge.ts).
 *  - V2 must NOT initiate its own Keycloak redirect or call sso/otp/send.
 */

/** Key used by v3SessionBridge to record that a vendor session was applied. */
export const EMBED_SESSION_APPLIED_KEY = 'v3_vendor_session_applied';

/**
 * Returns true when this page is rendered inside an iframe.
 *
 * Accessing `window.top` from a cross-origin iframe throws a SecurityError in
 * some browsers; the catch clause treats that as "we are in an iframe".
 */
export function isEmbeddedMode(): boolean {
  try {
    return globalThis.self !== globalThis.top;
  } catch {
    return true;
  }
}

/**
 * Returns true after v3SessionBridge has applied a V3_VENDOR_SESSION message
 * and reloaded the page. The flag is stored in sessionStorage so it resets on
 * a full tab close but survives the programmatic reload inside the iframe.
 */
export function isEmbedSessionApplied(): boolean {
  return sessionStorage.getItem(EMBED_SESSION_APPLIED_KEY) === 'true';
}

/** Popup / download / OIDC routes that must stay on the V2 origin when top-level. */
export function isV2OnlyTopLevelPath(pathname: string): boolean {
  return (
    pathname === '/rich-text-box-popup' ||
    pathname === '/silent-check-sso'
  );
}

/**
 * Ctrl/Cmd+click and "Open in new tab" load V2 as a top-level page (no shell).
 * When `REACT_APP_V3_SHELL_ORIGIN` is set, bounce to that origin with the same
 * path so the V3 wrapper always owns the tab. No-op inside the iframe.
 *
 * @returns true when a redirect was started (caller should skip bootstrapping).
 */
export function redirectTopLevelToV3Shell(): boolean {
  if (isEmbeddedMode()) return false;

  const shellOrigin = process.env.REACT_APP_V3_SHELL_ORIGIN?.replace(/\/$/, '');
  if (!shellOrigin) return false;

  const { pathname, search, hash } = globalThis.location;
  if (isV2OnlyTopLevelPath(pathname) || globalThis.opener) return false;

  globalThis.location.replace(`${shellOrigin}${pathname}${search}${hash}`);
  return true;
}
